import {
  AuditAction,
  AuditEntityType,
  AuditLogDto,
  CONTRACT_STATUSES,
  ContractStatus,
  INVOICE_PRINT_FORM_DATE_VIEW_STATUSES,
  LEADS_STATUSES,
  LeadStatus,
  NormalizedAuditPayload,
  PrintFormDateView,
} from '@sr/dto';
import { formatDateTime } from 'utils/date-format-helpers';

export const AUDIT_ACTION_LABELS: Partial<Record<AuditAction, string>> = {
  [AuditAction.COMPANY_CREATED]: 'Создана компания',
  [AuditAction.COMPANY_RENAMED]: 'Переименована компания',
  [AuditAction.COMPANY_MANAGER_LINKED]: 'Менеджер привязан к компании',
  [AuditAction.COMPANY_MANAGER_UNLINKED]: 'Менеджер откреплен от компании',
  [AuditAction.COMPANY_LEAD_LINKED]: 'Лид привязан к компании',

  [AuditAction.CONTRACT_UPDATED]: 'Обновлен договор',
  [AuditAction.LEGAL_ENTITY_UPDATED]: 'Обновлено юрлицо',

  [AuditAction.MEDIA_CREATED]: 'Создано СМИ',
  [AuditAction.MEDIA_UPDATED]: 'Обновлено СМИ',

  [AuditAction.PAYORDER_CREATED]: 'Создано платежное поручение',
  [AuditAction.PAYORDER_UPDATED]: 'Обновлено платежное поручение',
  [AuditAction.PAYORDER_STATUS_CHANGED]: 'Изменен статус платежного поручения',

  [AuditAction.PURCHASE_CREATED]: 'Создана заявка',
  [AuditAction.PURCHASE_STATUS_CHANGED]: 'Изменен статус заявки',
  [AuditAction.PURCHASE_WCC_AUTOMATION_FAILED]: 'Ошибка автоматического создания акта поставщика',

  [AuditAction.LEAD_CREATED]: 'Создан лид',
  [AuditAction.LEAD_UPDATED]: 'Изменён лид',
  [AuditAction.LEAD_MANAGER_CHANGED]: 'Изменён менеджер лида',
  [AuditAction.LEAD_MEDIAPLAN_UPDATED]: 'Изменён медиаплан лида',
  [AuditAction.LEAD_DELETED]: 'Удалён лид',
  [AuditAction.LEAD_RESTORED]: 'Восстановлен лид',
  [AuditAction.LEAD_CONTACT_CREATED]: 'Добавлен контакт лида',
  [AuditAction.LEAD_CONTACT_UPDATED]: 'Изменён контакт лида',
  [AuditAction.LEAD_CONTACT_DELETED]: 'Удалён контакт лида',
  [AuditAction.LEAD_CONTACT_RESTORED]: 'Восстановлен контакт лида',
  [AuditAction.LEAD_COMMENT_CREATED]: 'Добавлен комментарий лида',
  [AuditAction.LEAD_COMMENT_UPDATED]: 'Изменён комментарий лида',
  [AuditAction.LEAD_COMMENT_DELETED]: 'Удалён комментарий лида',
  [AuditAction.LEAD_COMMENT_RESTORED]: 'Восстановлен комментарий лида',
  [AuditAction.LEAD_EVENT_CREATED]: 'Добавлено событие лида',
  [AuditAction.LEAD_EVENT_UPDATED]: 'Изменено событие лида',
  [AuditAction.LEAD_EVENT_DELETED]: 'Удалено событие лида',
  [AuditAction.LEAD_EVENT_RESTORED]: 'Восстановлено событие лида',
  [AuditAction.LEAD_STATUS_CHANGED]: 'Изменен статус лида',
  [AuditAction.LEAD_COMPANY_LINKED]: 'Компания привязана к лиду',

  [AuditAction.INVOICE_CREATED]: 'Создан счет',
  [AuditAction.INVOICE_UPDATED]: 'Обновлен счет',

  [AuditAction.USER_UPDATED]: 'Обновлен пользователь',
};

const PAYLOAD_LABELS: Record<string, string> = {
  id: 'Номер записи',
  subject: 'Тема',
  leadId: 'Лид',
  targetId: 'Карточка',
  targetType: 'Тип карточки',
  commentType: 'Тип комментария',
  author: 'Автор',
  companyId: 'Компания',
  personId: 'Контактное лицо',
  contactPersonId: 'Контактное лицо',
  dueDate: 'Срок выполнения',
  important: 'Важное',
  completed: 'Выполнено',
  taskType: 'Тип события',
  type: 'Тип',
  atsEntryId: 'Звонок АТС',
  atsDuration: 'Длительность звонка',
  atsAudioLink: 'Запись звонка',
  fromPhoneId: 'Номер отправителя',
  toPhoneId: 'Номер получателя',
  wazzupMessageId: 'Сообщение Wazzup',
  wazzupChannelId: 'Канал Wazzup',
  wazzupChatType: 'Мессенджер',
  wazzupChatId: 'Чат Wazzup',
  wazzupContactName: 'Имя контакта Wazzup',
  wazzupPhone: 'Телефон Wazzup',
  wazzupLastInboundAt: 'Последнее входящее сообщение',
  sourceDirection: 'Направление',
  urlTypeId: 'Тип ссылки',
  phone: 'Телефон',
  email: 'Email',
  url: 'Ссылка',
  content: 'Содержание',
  userId: 'Менеджер',
  budgetId: 'Бюджет',
  qualityId: 'Качество',
  createdAt: 'Дата создания',
  deletedAt: 'Дата удаления',
  sourceType: 'Канал',
  sourceValue: 'Адрес / номер источника',
  source2Id: 'Правило источника',
  source2Name: 'Источник',
  mediaplanCityId: 'Город медиаплана',
  mediaplanMediaTypeId: 'Тип СМИ медиаплана',
  mediaplanCreatedAt: 'Дата медиаплана',
  mediaplanCreatedById: 'Автор медиаплана',
  mediaplanFileId: 'Файл медиаплана',
  mediaplanSum: 'Сумма медиаплана',
  transcription: 'Транскрипция',
  transcriptionStatus: 'Статус транскрибации',
  phoneId: 'Телефон',
  emailId: 'Email',
  urlId: 'Ссылка',
  phoneExt: 'Добавочный номер',
  relatedId: 'Номер записи',
  table: 'Тип записи',
  source: 'Операция',
  name: 'Название',
  previousName: 'Прошлое название',
  managerId: 'Менеджер',
  managerName: 'Менеджер',
  linkType: 'Тип связи',
  reason: 'Причина',
  status: 'Новый статус',
  previousStatus: 'Предыдущий статус',
  firstName: 'Имя',
  middleName: 'Отчество',
  secondName: 'Фамилия',
  login: 'Логин',
  isActive: 'Активен',
  isFired: 'Уволен',
  dob: 'Дата рождения',
  passportData: 'Паспортные данные',
  residenceAddress: 'Адрес проживания',
  address: 'Адрес',
  postAddress: 'Почтовый адрес',
  position: 'Должность',
  baseSalary: 'Оклад',
  salesPlan: 'План продаж',
  defaultManagerPercent: 'Процент менеджера',
  bonusFundRate: 'Процент бонусного фонда',
  remoteAccess: 'Удаленный доступ',
  remoteAccessIP: 'IP удаленного доступа',
  statusId: 'Статус',
  totalAmount: 'Сумма',
  accountId: 'Счет',
  paymentDate: 'Дата платежа',
  changes: 'Изменения',
  supervisorId: 'Руководитель',
  salesDepartmentId: 'Отдел продаж',
  roles: 'Роли',
  sellerLegalEntityId: 'Юрлицо продавца',
  buyerLegalEntityId: 'Юрлицо покупателя',
  contractTemplateId: 'Шаблон договора',
  sellerSignerId: 'Подписант продавца',
  buyerSignerId: 'Подписант покупателя',
  sellerBankDetailsId: 'Реквизиты продавца',
  buyerBankDetailsId: 'Реквизиты покупателя',
  legalEntityTypeId: 'Тип юрлица',
  attachmentFileId: 'Файл договора',
  stampFileId: 'Печать',
  autoprolong: 'Автопродление',
  contractEndDate: 'Дата окончания договора',
  vat: 'НДС',
  generateWcc: 'Формировать акт',
  invoiceSupplier: 'Счет поставщика',
  printFormShowMediaLink: 'Выводить ссылку на СМИ в печатных формах',
  printFormShowDateViewState: 'Даты оказания услуг',
  documentNumber: 'Номер',
};

type RawChangeValue = {
  previous: unknown;
  current: unknown;
};

export type ChangeValue = {
  previous: string;
  current: string;
};

export type PayloadEntry =
  | { type: 'text'; label: string; value: string }
  | { type: 'change'; label: string; field: string; change: ChangeValue };

export const buildUserName = (user?: { firstName?: string; middleName?: string; secondName?: string }) => {
  if (!user) return null;
  return [user.firstName, user.middleName, user.secondName].filter(Boolean).join(' ').trim() || null;
};

export const buildActorName = (actor?: { name?: string }) => {
  if (!actor) return null;
  return actor.name ?? null;
};

export const buildPayloadEntries = (entityType: AuditEntityType, payload: AuditLogDto['payload']): PayloadEntry[] => {
  const data = extractPayloadData(payload);
  const labels = extractPayloadLabels(payload);
  if (!data) return [];

  const entries: PayloadEntry[] = [];

  Object.entries(data).forEach(([key, value]) => {
    if (['source', 'nameFormat', 'manual'].includes(key)) return;
    if (key.endsWith('Id')) {
      const nameKey = `${key.slice(0, -2)}Name`;
      if (nameKey in data) {
        return;
      }
    }
    if (key === 'record' && entityType === AuditEntityType.LEAD && value && typeof value === 'object') {
      entries.push(...buildPayloadEntries(entityType, { entity: { type: entityType, id: 0 }, data: { ...value, labels } }));
      return;
    }
    if (key === 'changes' && isChangeSet(value)) {
      entries.push(...buildChangeEntries(entityType, value, labels));
      return;
    }

    const formattedValue = formatPayloadValue(entityType, key, value, labels);
    if (formattedValue === '') return;
    entries.push({
      type: 'text',
      label: PAYLOAD_LABELS[key] ?? key,
      value: formattedValue,
    });
  });

  return entries;
};

const extractPayloadData = (payload: AuditLogDto['payload']): Record<string, unknown> | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if (isNormalizedPayload(payload)) {
    if (!payload.data) return null;
    const data = { ...payload.data };
    delete data.labels;
    return Object.keys(data).length ? data : null;
  }

  const legacyPayload = { ...(payload as Record<string, unknown>) };
  delete legacyPayload.actor;
  delete legacyPayload.entity;
  delete legacyPayload.labels;
  return Object.keys(legacyPayload).length ? legacyPayload : null;
};

type AuditPayloadLabels = Record<string, Record<string, string>>;

const extractPayloadLabels = (payload: AuditLogDto['payload']): AuditPayloadLabels | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  if (isNormalizedPayload(payload)) {
    const data = payload.data as Record<string, unknown> | undefined;
    const labels = data?.labels;
    return isLabelMap(labels) ? (labels as AuditPayloadLabels) : null;
  }
  const labels = (payload as Record<string, unknown>).labels;
  return isLabelMap(labels) ? (labels as AuditPayloadLabels) : null;
};

const isLabelMap = (value: unknown): value is AuditPayloadLabels => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  return Object.values(value as Record<string, unknown>).every((entry) => !!entry && typeof entry === 'object' && !Array.isArray(entry));
};

const isNormalizedPayload = (payload: AuditLogDto['payload']): payload is NormalizedAuditPayload => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  return 'entity' in payload && typeof (payload as { entity?: unknown }).entity === 'object';
};

const isChangeSet = (value: unknown): value is Record<string, RawChangeValue> => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return Object.values(value).every((item) => isChangeValue(item));
};

const isChangeValue = (value: unknown): value is RawChangeValue => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return 'current' in value && 'previous' in value;
};

const buildChangeEntries = (entityType: AuditEntityType, changes: Record<string, RawChangeValue>, labels?: AuditPayloadLabels | null): PayloadEntry[] => {
  return Object.entries(changes).map(([field, change]) => ({
    type: 'change',
    label: PAYLOAD_LABELS[field] ?? field,
    field,
    change: {
      previous: formatPayloadValue(entityType, field, change.previous, labels),
      current: formatPayloadValue(entityType, field, change.current, labels),
    },
  }));
};

const formatPayloadValue = (entityType: AuditEntityType, key: string, value: unknown, labels?: AuditPayloadLabels | null) => {
  const leadStatusValue = formatLeadStatusValue(entityType, key, value);
  if (leadStatusValue !== undefined) {
    return leadStatusValue;
  }
  const contractStatusValue = formatContractStatusValue(entityType, key, value);
  if (contractStatusValue !== undefined) {
    return contractStatusValue;
  }
  const reasonValue = formatReasonValue(key, value);
  if (reasonValue !== undefined) {
    return reasonValue;
  }
  const printFormDateViewValue = formatPrintFormDateViewValue(key, value);
  if (printFormDateViewValue !== undefined) {
    return printFormDateViewValue;
  }
  if (value === null || value === undefined) return '—';
  const mappedValue = formatValueFromLabels(key, value, labels);
  if (mappedValue !== undefined) {
    return mappedValue;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'string') {
    if (key.toLowerCase().includes('date')) {
      return formatDateTime(value);
    }
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const formatValueFromLabels = (key: string, value: unknown, labels?: AuditPayloadLabels | null) => {
  if (!labels) return undefined;
  const mapping = labels[key];
  if (!mapping) return undefined;
  if (Array.isArray(value)) {
    return value.map((item) => mapping[String(item)] ?? String(item)).join(', ');
  }
  return mapping[String(value)];
};

const LEAD_STATUS_PAYLOAD_KEYS = new Set(['status', 'previousStatus', 'statusId']);
const CONTRACT_STATUS_PAYLOAD_KEYS = new Set(['status', 'previousStatus']);
const REASON_VALUE_LABELS: Record<string, string> = {
  'company-is-not-client': 'Компания не является клиентом',
};
const PRINT_FORM_DATE_VIEW_KEYS = new Set(['printFormShowDateViewState']);

const formatLeadStatusValue = (entityType: AuditEntityType, key: string, value: unknown) => {
  if (entityType !== AuditEntityType.LEAD) {
    return undefined;
  }
  if (!LEAD_STATUS_PAYLOAD_KEYS.has(key)) {
    return undefined;
  }
  if (typeof value === 'number') {
    return LEADS_STATUSES[value as LeadStatus] ?? String(value);
  }
  return typeof value === 'string' ? value : undefined;
};

const formatContractStatusValue = (entityType: AuditEntityType, key: string, value: unknown) => {
  if (entityType !== AuditEntityType.CONTRACT) {
    return undefined;
  }
  if (!CONTRACT_STATUS_PAYLOAD_KEYS.has(key)) {
    return undefined;
  }
  if (typeof value === 'number') {
    return CONTRACT_STATUSES[value as ContractStatus] ?? String(value);
  }
  if (typeof value === 'string') {
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      return CONTRACT_STATUSES[numeric as ContractStatus] ?? value;
    }
    return value;
  }
  return undefined;
};

const formatReasonValue = (key: string, value: unknown) => {
  if (key !== 'reason') {
    return undefined;
  }
  if (typeof value === 'string') {
    return REASON_VALUE_LABELS[value] ?? value;
  }
  return undefined;
};

const formatPrintFormDateViewValue = (key: string, value: unknown) => {
  if (!PRINT_FORM_DATE_VIEW_KEYS.has(key)) {
    return undefined;
  }
  if (typeof value === 'number') {
    return INVOICE_PRINT_FORM_DATE_VIEW_STATUSES[value as PrintFormDateView] ?? String(value);
  }
  if (typeof value === 'string') {
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      return INVOICE_PRINT_FORM_DATE_VIEW_STATUSES[numeric as PrintFormDateView] ?? value;
    }
    return value;
  }
  return undefined;
};

const compactName = (name?: string) => name?.trim().split(/\s+/).slice(0, 2).join(' ') || null;

export const buildLeadAuditActor = (log: AuditLogDto): string => {
  const actor = log.payload?.actor;
  if (actor?.secondName || actor?.firstName) return [actor.secondName, actor.firstName].filter(Boolean).join(' ');
  if (actor?.name) return compactName(actor.name) ?? 'Система';
  return [log.user?.secondName, log.user?.firstName].filter(Boolean).join(' ') || 'Система';
};

export const buildLeadAuditDetails = (log: AuditLogDto): string => {
  const data = extractPayloadData(log.payload) ?? {};
  const entries = buildPayloadEntries(AuditEntityType.LEAD, log.payload);
  if (log.action === AuditAction.LEAD_STATUS_CHANGED) {
    const changes = data.changes as Record<string, RawChangeValue> | undefined;
    const before = formatPayloadValue(AuditEntityType.LEAD, 'status', data.previousStatus ?? changes?.statusId?.previous);
    const after = formatPayloadValue(AuditEntityType.LEAD, 'status', data.status ?? data.statusId ?? changes?.statusId?.current);
    return `${before} → ${after}`;
  }
  const shortManager = (value: string) => {
    if (value === '—' || /^\d+$/.test(value)) return value;
    const parts = value.trim().split(/\s+/);
    // Previous lead hooks saved manager labels as first name, patronymic, surname.
    return data.nameFormat === 'surname-first' ? compactName(value) : `${parts[parts.length - 1]} ${parts[0]}`;
  };
  if (log.action === AuditAction.LEAD_MANAGER_CHANGED) {
    const manager = entries.find((entry) => entry.type === 'change' && entry.field === 'userId');
    return manager?.type === 'change' ? `${shortManager(manager.change.previous)} → ${shortManager(manager.change.current)}` : '';
  }
  const meaningful = new Set(['Тема', 'Содержание', 'Телефон', 'Email', 'Ссылка', 'Компания', 'Город медиаплана', 'Тип СМИ медиаплана', 'Файл медиаплана', 'Сумма медиаплана', 'Бюджет', 'Качество', 'Транскрипция', 'Статус транскрибации', 'Последнее входящее сообщение', 'Источник', 'Имя контакта Wazzup', 'Срок выполнения', 'Важное', 'Выполнено']);
  const details = entries.filter((entry) => entry.type === 'change' || meaningful.has(entry.label));
  return details.map((entry) => entry.type === 'change'
    ? `${details.length > 1 ? `${entry.label}: ` : ''}${entry.change.previous} → ${entry.change.current}`
    : `${details.length > 1 ? `${entry.label}: ` : ''}${entry.value}`).join('; ').replace(/\s+/g, ' ').trim();
};
